/**
 * Pure, immutable edit operations over a definition tree. The editor's state
 * is one BuilderDefinition; every interaction maps to one of these. Paths use
 * the runner's cursor grammar: [1] top-level; [1,'cases',0,'steps',2] a case
 * arm; [3,'onApprove',0] an approval arm.
 */
import type {
  BuilderBranchCase,
  BuilderDefinition,
  BuilderStep,
  WorkflowPersona,
  WorkflowTrigger,
} from '../../lib/types.js';

export type StepPath = (string | number)[];

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/** The mutable list a container path addresses inside a cloned tree. */
const listAt = (def: BuilderDefinition, containerPath: StepPath): BuilderStep[] => {
  let node: unknown = def.steps;
  for (const seg of containerPath) {
    node = (node as Record<string | number, unknown>)[seg];
  }
  return node as BuilderStep[];
};

/** How many steps sit in a container — the appended step's index is one less. */
export const listLengthAt = (def: BuilderDefinition, containerPath: StepPath): number =>
  listAt(def, containerPath).length;

export const stepAt = (def: BuilderDefinition, path: StepPath): BuilderStep | null =>
  listAt(def, path.slice(0, -1))[path[path.length - 1] as number] ?? null;

export const replaceStep = (
  def: BuilderDefinition,
  path: StepPath,
  step: BuilderStep,
): BuilderDefinition => {
  const next = clone(def);
  listAt(next, path.slice(0, -1))[path[path.length - 1] as number] = step;
  return next;
};

export const insertStep = (
  def: BuilderDefinition,
  containerPath: StepPath,
  step: BuilderStep,
  index?: number,
): BuilderDefinition => {
  const next = clone(def);
  const list = listAt(next, containerPath);
  list.splice(index ?? list.length, 0, step);
  return next;
};

export const removeStep = (def: BuilderDefinition, path: StepPath): BuilderDefinition => {
  const next = clone(def);
  listAt(next, path.slice(0, -1)).splice(path[path.length - 1] as number, 1);
  return next;
};

export const moveStep = (
  def: BuilderDefinition,
  path: StepPath,
  direction: -1 | 1,
): BuilderDefinition => {
  const next = clone(def);
  const list = listAt(next, path.slice(0, -1));
  const i = path[path.length - 1] as number;
  const j = i + direction;
  if (j < 0 || j >= list.length) return def;
  const a = list[i];
  const b = list[j];
  if (!a || !b) return def;
  list[i] = b;
  list[j] = a;
  return next;
};

export const addCase = (def: BuilderDefinition, branchPath: StepPath): BuilderDefinition => {
  const next = clone(def);
  const branch = stepAt(next, branchPath);
  if (branch?.type !== 'branch') return def;
  branch.cases.push({
    label: `Case ${String(branch.cases.length + 1)}`,
    when: { match: 'all', conditions: [{ path: '', op: 'eq', value: '' }] },
    steps: [],
  });
  return next;
};

export const removeCase = (
  def: BuilderDefinition,
  branchPath: StepPath,
  caseIndex: number,
): BuilderDefinition => {
  const next = clone(def);
  const branch = stepAt(next, branchPath);
  if (branch?.type !== 'branch' || branch.cases.length <= 1) return def; // ≥1 case required
  branch.cases.splice(caseIndex, 1);
  return next;
};

export const updateCase = (
  def: BuilderDefinition,
  branchPath: StepPath,
  caseIndex: number,
  patch: Partial<BuilderBranchCase>,
): BuilderDefinition => {
  const next = clone(def);
  const branch = stepAt(next, branchPath);
  if (branch?.type !== 'branch') return def;
  const kase = branch.cases[caseIndex];
  if (!kase) return def;
  branch.cases[caseIndex] = { ...kase, ...patch };
  return next;
};

/** Whether a step of this type appears anywhere in the tree, arms included. */
export const containsStep = (steps: BuilderStep[], type: BuilderStep['type']): boolean =>
  steps.some(
    (s) =>
      s.type === type ||
      (s.type === 'branch' &&
        (s.cases.some((c) => containsStep(c.steps, type)) || containsStep(s.otherwise, type))) ||
      (s.type === 'approval' &&
        (containsStep(s.onApprove, type) || containsStep(s.onDecline, type))),
  );

/** Step types the builder can edit (lookups come from the bot); a missing type fails typecheck. */
const KNOWN_STEP_TYPES: Record<Exclude<BuilderStep['type'], 'lookup'>, true> = {
  filter: true,
  notify: true,
  agent: true,
  approval: true,
  report: true,
  bot: true,
  branch: true,
};

/**
 * The server's step menu, minus types this build doesn't know. The server can learn a step
 * before an open tab reloads; offering it there would insert an empty step and crash the page.
 */
export const knownStepTypes = <T extends { type: string }>(types: readonly T[]): T[] =>
  types.filter((t) => Object.hasOwn(KNOWN_STEP_TYPES, t.type));

/** The event field that obviously names a person: its only email field, or '' when unclear. */
export const obviousPersonField = (fields: readonly { path: string; label: string }[]): string => {
  const emails = fields.filter((f) => /e-?mail/i.test(`${f.path} ${f.label}`));
  return emails.length === 1 ? (emails[0]?.path ?? '') : '';
};

/** Fresh default step per type — what "+ Add step" inserts. */
export const defaultStep = (type: BuilderStep['type']): BuilderStep => {
  switch (type) {
    case 'filter':
      return { type, match: 'all', conditions: [{ path: '', op: 'eq', value: '' }] };
    case 'notify':
      return { type, channelIds: [], title: '', body: '', severity: 'info' };
    case 'agent':
      // No fields: the persona declares what it answers with.
      return { type, personaId: '', task: '', reportOn: [] };
    case 'approval':
      return { type, approvers: [], title: '', onApprove: [], onDecline: [] };
    case 'report':
      return {
        type,
        templateId: '',
        customer: '',
        region: '',
        units: '',
        period: 'yesterday',
        sendLink: true,
        recipients: [],
      };
    case 'bot':
      return {
        type,
        botId: '',
        recipients: { kind: 'from_event', field: '' },
        goal: '',
      };
    case 'branch':
      return {
        type,
        cases: [
          {
            label: 'Case 1',
            when: { match: 'all', conditions: [{ path: '', op: 'eq', value: '' }] },
            steps: [],
          },
        ],
        otherwise: [],
      };
    case 'lookup':
      return { type, tool: '', input: {} };
  }
};

/** Depth of a container path (0 = top level) — the UI blocks forks past depth 2. */
export const forkDepth = (containerPath: StepPath): number =>
  containerPath.filter(
    (s) => s === 'cases' || s === 'onApprove' || s === 'onDecline' || s === 'otherwise',
  ).length;

/** 'feedbackId' → 'Feedback ID'. Field names come from whoever sends the
 * event; nobody should have to read camelCase to build a workflow. */
export const humanizeKey = (key: string): string => {
  const words = key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  const sentence = words.charAt(0).toUpperCase() + words.slice(1);
  return sentence.replace(/\bid\b/gi, 'ID').replace(/\burl\b/gi, 'URL');
};

/**
 * Every option carries a hint line — uniform two-line rows are what lets the
 * eye scan the list (a mix of one- and two-line rows reads as noise). Enums
 * match the drawer's type-cast pills; the rest fall back to their kind.
 */
const describeKind = (kind: string, values?: string[]): string => {
  switch (kind) {
    case 'enum':
      return values?.length ? `one of ${values.join(' / ')}` : 'one of a list';
    case 'boolean':
      return 'yes or no';
    case 'number':
      return 'a number';
    default:
      return 'text';
  }
};

export interface PickerVariable {
  /** Stored form, e.g. 'payload.unitId' or 'steps.classify.result.kind'. */
  path: string;
  /** Friendly label — the value's own name; provenance lives in `source`. */
  label: string;
  /** Display-ready hint ("a number", "one of low / medium / high"). */
  sample?: string;
  values?: string[];
  kind?: WorkflowTrigger['fields'][number]['kind'];
  /** Where it comes from — pickers render this as a section header ("From
   * step “Handoff”"), stated once per source instead of suffixed per item. */
  source: 'event' | { step: string };
}

/**
 * The variable picker's options: the trigger's REGISTERED fields plus earlier steps' declared outputs on the current branch — agent outputs. "Prior" = anywhere before in document order.
 * Registered fields only, never sampled from received events: the runtime
 * projects every payload down to the declared list before a step sees it,
 * so an undeclared field would resolve to nothing at run time.
 */
export const pickerVariables = (
  def: BuilderDefinition,
  beforePath: StepPath,
  triggerFields: WorkflowTrigger['fields'] = [],
  personas: WorkflowPersona[] = [],
): PickerVariable[] => {
  const vars: PickerVariable[] = [];
  for (const f of triggerFields) {
    vars.push({
      path: `payload.${f.name}`,
      label: f.label || humanizeKey(f.name),
      kind: f.kind,
      values: f.values,
      sample: describeKind(f.kind, f.values),
      source: 'event',
    });
  }
  const walk = (steps: BuilderStep[], prefix: StepPath): void => {
    const target = beforePath[prefix.length];
    if (typeof target !== 'number') return;
    steps.forEach((step, i) => {
      const path = [...prefix, i];
      if (i > target) return;
      if (i === target) {
        const arm = beforePath[path.length];
        if (step.type === 'branch') {
          if (arm === 'cases') {
            const index = beforePath[path.length + 1];
            if (typeof index === 'number' && step.cases[index])
              walk(step.cases[index].steps, [...path, 'cases', index, 'steps']);
          } else if (arm === 'otherwise') walk(step.otherwise, [...path, 'otherwise']);
        } else if (step.type === 'approval') {
          if (arm === 'onApprove') walk(step.onApprove, [...path, 'onApprove']);
          else if (arm === 'onDecline') walk(step.onDecline, [...path, 'onDecline']);
        }
        return;
      }
      // Answers are addressed by the step's NAME (never its position — that
      // moves when steps are reordered). An unnamed step offers nothing.
      // An agent's answers come from its PERSONA, not the step — the author
      // never declared them, so the picker reads the catalog the same way it
      // reads a tool's outputs.
      if (step.type === 'agent' && step.name?.trim()) {
        const handle = step.name.trim();
        for (const o of personas.find((pp) => pp.id === step.personaId)?.outputs ?? []) {
          vars.push({
            path: `steps.${handle}.result.${o.name}`,
            label: humanizeKey(o.name),
            kind: o.kind,
            values: o.values,
            sample: describeKind(o.kind, o.values),
            source: { step: handle },
          });
        }
      }
    });
  };
  walk(def.steps, []);
  return vars;
};

/** Named agent steps BEFORE a path in document order — what a report step may
 * cite as its prose source (the runtime resolves from prior outputs only). */
export const priorAgentSteps = (def: BuilderDefinition, beforePath: StepPath): string[] => {
  const names: string[] = [];
  const walk = (steps: BuilderStep[], prefix: StepPath): void => {
    const target = beforePath[prefix.length];
    if (typeof target !== 'number') return;
    steps.forEach((step, i) => {
      const path = [...prefix, i];
      if (i > target) return;
      if (i === target) {
        const arm = beforePath[path.length];
        if (step.type === 'branch') {
          if (arm === 'cases') {
            const index = beforePath[path.length + 1];
            if (typeof index === 'number' && step.cases[index])
              walk(step.cases[index].steps, [...path, 'cases', index, 'steps']);
          } else if (arm === 'otherwise') walk(step.otherwise, [...path, 'otherwise']);
        } else if (step.type === 'approval') {
          if (arm === 'onApprove') walk(step.onApprove, [...path, 'onApprove']);
          else if (arm === 'onDecline') walk(step.onDecline, [...path, 'onDecline']);
        }
        return;
      }
      if (step.type === 'agent' && step.name?.trim()) names.push(step.name.trim());
    });
  };
  walk(def.steps, []);
  return names;
};
